import type { FilterOption } from '@/components/common-table/FilterSearchBar'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { type CustomViewsConfig, CustomViewsContext } from './custom-views-context'

export { useCustomViewsContext } from './custom-views-context'
export type { CustomViewsConfig } from './custom-views-context'

export function CustomViewsProvider({ children }: { children: ReactNode }) {
	const [config, setConfig] = useState<CustomViewsConfig | null>(null)
	const [isViewCreatorOpen, setIsViewCreatorOpen] = useState(false)
	const [editingViewId, setEditingViewId] = useState<string | null>(null)
	const [activeViewId, setActiveViewId] = useState<string | null>(null)

	const [currentFilters, setCurrentFilters] = useState<FilterOption[]>([])
	const [rightContent, setRightContent] = useState<ReactNode>(null)
	const [clearFiltersFn, setClearFiltersFn] = useState<(() => void) | null>(null)

	const onOpenChange = (isOpen: boolean) => {
		setIsViewCreatorOpen(isOpen)
	}

	const registerClearFilters = useCallback((fn: (() => void) | null) => {
		setClearFiltersFn(() => fn)
	}, [])

	const clearRegularFilters = useCallback(() => {
		if (clearFiltersFn) {
			clearFiltersFn()
		}
	}, [clearFiltersFn])

	const onCustomViewFiltersChange = config?.onCustomViewFiltersChange

	// Sync modal filter changes to the page's active filter handler
	// This ensures the table updates in real-time as users add/remove filters in the modal
	useEffect(() => {
		if (isViewCreatorOpen && onCustomViewFiltersChange) {
			onCustomViewFiltersChange(currentFilters)
		}
	}, [currentFilters, isViewCreatorOpen, onCustomViewFiltersChange])

	return (
		<CustomViewsContext.Provider
			value={{
				config,
				setConfig,
				isViewCreatorOpen,
				setIsViewCreatorOpen,
				currentFilters,
				setCurrentFilters,
				onOpenChange,
				editingViewId,
				setEditingViewId,
				rightContent,
				setRightContent,
				registerClearFilters,
				clearRegularFilters,
				activeViewId,
				setActiveViewId,
			}}>
			{children}
		</CustomViewsContext.Provider>
	)
}
